import { stegaClean } from "@sanity/client/stega";

import { cn } from "./cn";

export type ColorTheme = "light" | "dark" | "primary" | "secondary" | "neutral" | "transparent";

/**
 * Maps color theme variants to Tailwind classes for background and text colors.
 * Uses design system tokens for consistency.
 */
export function getColorThemeClasses(theme?: ColorTheme): {
  background: string;
  text: string;
} {
  switch (theme) {
    case "dark":
      return {
        background: "bg-neutral-darkest",
        text: "text-white",
      };
    case "primary":
      return {
        background: "bg-emerald-dark",
        text: "text-white",
      };
    case "secondary":
      return {
        background: "bg-indigo-dark",
        text: "text-white",
      };
    case "neutral":
      return {
        background: "bg-neutral",
        text: "text-neutral-darkest",
      };
    case "transparent":
      return {
        background: "",
        text: "text-foreground",
      };
    case "light":
    default:
      return {
        background: "bg-white",
        text: "text-neutral-darkest",
      };
  }
}

/**
 * Applies color theme classes to a component.
 * Returns combined className string for background and text.
 */
export function applyColorTheme(theme: ColorTheme | undefined, additionalClasses?: string): string {
  const { background, text } = getColorThemeClasses(theme);
  return cn(background, text, additionalClasses);
}
/**
 * Gets prose classes for PortableText components based on theme.
 * Returns comprehensive prose color classes for all prose elements.
 *
 * @param isDarkTheme - Whether the theme is dark (dark, primary, or secondary)
 * @returns Combined className string with prose color classes
 */

export function getProseClasses(isDarkTheme: boolean, forceClassImportance?: boolean): string {
  if (forceClassImportance) {
    return cn(
      isDarkTheme
        ? "**:text-white! marker:text-white!"
        : "**:text-neutral-darkest! marker:text-neutral-darkest!",
    );
  }

  return cn(
    isDarkTheme
      ? "**:text-white marker:text-white"
      : "**:text-neutral-darkest marker:text-neutral-darkest",
  );
} /**
 * Extracts color theme information from a module and returns theme-related classes and flags.
 * Useful for components that need to apply custom backgrounds while maintaining theme text colors.
 *
 * @param module - Module object with optional colorTheme property
 * @param className - Optional additional class names to include in themeClasses (default: "")
 * @param forceClassImportance - Optional flag to force ! on all classes (default: false)
 * @returns Object containing:
 *   - colorTheme: The resolved color theme
 *   - themeClasses: Full theme classes (background + text + additional classes) - use when you want the theme background
 *   - themeTextClasses: Only text color classes - use when you have a custom background (e.g., gradient)
 *   - proseClasses: Prose classes for PortableText components - includes all prose element color classes
 *   - isDarkTheme: Boolean indicating if the theme is dark (dark, primary, or secondary)
 */

export function getModuleColorTheme(
  module: { colorTheme?: ColorTheme },
  className: string = "",
  forceClassImportance: boolean = false,
) {
  const colorTheme = stegaClean(module.colorTheme ?? "dark") as ColorTheme;
  const themeClasses = applyColorTheme(colorTheme, className);
  const { text: themeTextClasses } = getColorThemeClasses(colorTheme);
  const isDarkTheme =
    colorTheme === "dark" || colorTheme === "primary" || colorTheme === "secondary";
  const proseClasses = getProseClasses(isDarkTheme, forceClassImportance);

  return {
    colorTheme,
    themeClasses, // Use this when you want the full theme (background + text + additional classes)
    themeTextClasses, // Use this when you have a custom background but want theme text colors
    proseClasses, // Use this for PortableText components to apply theme-appropriate prose colors
    isDarkTheme,
  };
}
